import React, { useContext, useEffect } from "react";
import { LayoutChangeEvent } from "react-native";
import { useEvents } from "../../../../hooks/useEvents";
import { useQaLabel } from "../../../../hooks/useQaLabel";
import { useTheme } from "../../../../theming";
import { TabsContext } from "../../tabs";
import {
  StyledPressable,
  StyledTextTitle,
  StyledViewIconContainer,
  StyledViewTitleContainer,
  StyledTextDescription
} from "./styled-components";
import { TabProps } from "./types";

export type { TabProps };

export const Tab = ({
  value,
  title,
  description,
  counter,
  isSelected = false,
  isDisabled = false,
  noBorder = false,
  isStretched,
  qaLabel = value,
  isTitleUppercase = false,
  hoverColor,
  textTitleColor,
  textTitleSelectedColor,
  hasPressedColor = true
}: TabProps) => {
  const viewTestProps = useQaLabel(qaLabel);
  const { colors } = useTheme();
  const {
    selectedValue,
    onChange = () => {},
    setTabsLayout
  } = useContext(TabsContext);
  const { events, setEvents, eventsHandlers } = useEvents({
    isSelected: selectedValue === value
  });

  useEffect(() => {
    setEvents({ ...events, isSelected });
  }, [isSelected]);

  useEffect(() => {
    setEvents({
      ...events,
      isSelected: isSelected || selectedValue === value
    });
  }, [selectedValue]);

  const pressHandler = () => {
    onChange(value);
  };

  const onLayoutHandler = (event: LayoutChangeEvent, tab: string) => {
    const { width, x } = event.nativeEvent.layout;

    const newState = {
      [tab]: { width, x }
    };

    setTabsLayout((state: {}) => ({ ...state, ...newState }));
  };

  return (
    <StyledPressable
      accessibilityRole="tab"
      onPress={pressHandler}
      isStretched={isStretched}
      isDisabled={isDisabled}
      noBorder={noBorder}
      disabled={isDisabled}
      onLayout={(event) => onLayoutHandler(event, value)}
      onLongPress={pressHandler}
      underlayColor={colors.blue_accent[100]}
      hoverColor={hoverColor}
      hasPressedColor={hasPressedColor}
      {...eventsHandlers}
      {...events}
      {...viewTestProps}
    >
      <>
        <StyledViewTitleContainer>
          <StyledTextTitle
            {...events}
            isDisabled={isDisabled}
            isTitleUppercase={isTitleUppercase}
            textTitleColor={textTitleColor}
            textTitleSelectedColor={textTitleSelectedColor}
          >
            {title}
          </StyledTextTitle>
          {!isDisabled && counter && (
            <StyledViewIconContainer>{counter}</StyledViewIconContainer>
          )}
        </StyledViewTitleContainer>
        {description && (
          <StyledTextDescription {...events} isDisabled={isDisabled}>
            {description}
          </StyledTextDescription>
        )}
      </>
    </StyledPressable>
  );
};
